import { create } from "zustand";
import type { IconName } from "@/components/ui/iconPaths";

export interface Toast {
  id: string;
  title: string;
  message?: string;
  icon?: IconName;
  /** Auto-dismiss delay in ms. */
  duration: number;
}

interface ToastState {
  toasts: Toast[];
  push: (toast: Omit<Toast, "id" | "duration"> & { duration?: number }) => string;
  dismiss: (id: string) => void;
  clear: () => void;
}

export const useToastStore = create<ToastState>((set) => ({
  toasts: [],

  push: ({ title, message, icon = "check", duration = 4200 }) => {
    const id = crypto.randomUUID();
    set((state) => ({ toasts: [...state.toasts, { id, title, message, icon, duration }] }));
    return id;
  },

  dismiss: (id) => set((state) => ({ toasts: state.toasts.filter((t) => t.id !== id) })),

  clear: () => set({ toasts: [] }),
}));

/** Imperative helper for use outside React (interceptors, stores). */
export const toast = {
  show: (title: string, message?: string, icon?: IconName) =>
    useToastStore.getState().push({ title, message, icon }),
  error: (title: string, message?: string) =>
    useToastStore.getState().push({ title, message, icon: "alert" }),
  success: (title: string, message?: string) =>
    useToastStore.getState().push({ title, message, icon: "check-circle" }),
};
